import { RefObject, useRef } from 'react';
import { useScroll, useTransform, MotionValue } from 'framer-motion';
import {
  SCROLL_REVEAL_VARIANTS,
  ScrollRevealVariant,
} from './scrollMotion';

export interface ScrollRevealMotion {
  opacity: MotionValue<number>;
  y: MotionValue<number>;
  filter?: MotionValue<string>;
}

export function useScrollReveal(
  variant: ScrollRevealVariant = 'content',
  fadeOut = true,
): {
  ref: RefObject<HTMLDivElement | null>;
  motion: ScrollRevealMotion;
} {
  const ref = useRef<HTMLDivElement>(null);
  const config = SCROLL_REVEAL_VARIANTS[variant];
  const hasBlur = config.blurEnter > 0 || config.blurExit > 0;

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: [config.offset[0], config.offset[1]] as NonNullable<
      Parameters<typeof useScroll>[0]
    >['offset'],
  });

  const opacityKeys = fadeOut
    ? [0, config.opacityIn, config.opacityOut, 1]
    : [0, config.opacityIn, 1, 1];
  const opacityRange = fadeOut ? [0, 1, 1, 0] : [0, 1, 1, 1];

  const motionKeys = fadeOut
    ? [0, config.opacityIn, config.opacityOut, 1]
    : [0, config.opacityIn, 1, 1];

  const yRange = fadeOut
    ? [config.yEnter, 0, 0, -config.yExit]
    : [config.yEnter, 0, 0, 0];

  const blurRange = fadeOut
    ? [config.blurEnter, 0, 0, config.blurExit]
    : [config.blurEnter, 0, 0, 0];

  const opacity = useTransform(scrollYProgress, opacityKeys, opacityRange);
  const y = useTransform(scrollYProgress, motionKeys, yRange);
  const blur = useTransform(scrollYProgress, motionKeys, blurRange);
  const filter = useTransform(blur, (value) =>
    value > 0 ? `blur(${value}px)` : 'none',
  );

  return {
    ref,
    motion: hasBlur ? { opacity, y, filter } : { opacity, y },
  };
}
